import { useEffect, useState } from 'react';
import {
  useGetCandidateAnswersQuery,
  useGetPreQualConfigQuery,
} from '../../../../../utils/api_service/preQualApi';
import { getVideoFromStorage } from '../../../../../utils/api_service/storage_api';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutlineRounded,
  CloseRounded,
  DescriptionOutlined,
  EmailOutlined,
  LocationOnOutlined,
  VideocamOutlined,
  WarningAmberRounded,
} from '@mui/icons-material';

interface PreQualResultsTabProps {
  jobOpeningId: string;
  jobApplicantId: string;
  handleResendInvite: (id: string) => void;
  isResending: boolean;
  preQualSentAt?: number | string;
}

const isVideoQuestionType = (questionType?: string) =>
  questionType === 'video-answer' || questionType === 'video';

const getVideoStoragePath = (value?: string | null) => {
  if (!value) return '';

  if (!value.startsWith('http')) {
    return value.replace(/^\/+/, '');
  }

  try {
    const url = new URL(value);
    const filePath = url.searchParams.get('file_path');
    if (filePath) return filePath;
    return decodeURIComponent(url.pathname.replace(/^\/+/, ''));
  } catch {
    return value;
  }
};

export const PreQualResultsTab: React.FC<PreQualResultsTabProps> = ({
  jobOpeningId,
  jobApplicantId,
  handleResendInvite,
  isResending,
  preQualSentAt,
}) => {
  // Fetch candidate answers
  const { data: answersData, isLoading: answersLoading } =
    useGetCandidateAnswersQuery(
      {
        jobOpeningId,
        jobApplicantId,
      },
      {
        skip: !jobOpeningId || !jobApplicantId,
      },
    );

  // Fetch pre-qual configuration as fallback for question labels
  const { data: configData, isLoading: configLoading } =
    useGetPreQualConfigQuery(jobOpeningId, {
      skip: !jobOpeningId,
    });

  const isLoading = answersLoading || configLoading;
  const [videoUrls, setVideoUrls] = useState<Record<string, string>>({});
  const [videoLoading, setVideoLoading] = useState<Record<string, boolean>>({});
  const [videoErrors, setVideoErrors] = useState<Record<string, boolean>>({});
  const [selectedVideo, setSelectedVideo] = useState<{
    url: string;
    question: string;
  } | null>(null);

  useEffect(() => {
    const videoAnswers = answersData?.videoAnswers || [];
    if (videoAnswers.length === 0) return;

    let active = true;

    videoAnswers.forEach((videoAnswer) => {
      const questionKey = videoAnswer.questionKey;
      const filePath = getVideoStoragePath(videoAnswer.videoKey);

      if (!questionKey || !filePath || videoUrls[questionKey]) return;

      setVideoLoading((prev) => ({ ...prev, [questionKey]: true }));
      setVideoErrors((prev) => ({ ...prev, [questionKey]: false }));

      getVideoFromStorage(filePath)
        .then((url) => {
          if (!active || !url) return;
          setVideoUrls((prev) => ({ ...prev, [questionKey]: url }));
        })
        .catch(() => {
          if (!active) return;
          setVideoErrors((prev) => ({ ...prev, [questionKey]: true }));
        })
        .finally(() => {
          if (!active) return;
          setVideoLoading((prev) => ({ ...prev, [questionKey]: false }));
        });
    });

    return () => {
      active = false;
    };
  }, [answersData?.videoAnswers, videoUrls]);

  if (isLoading) {
    return (
      <Box
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
        }}
      >
        <CircularProgress size={48} sx={{ color: '#4f46e5', mb: 2 }} />
        <Typography
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
            fontSize: '13px',
          }}
        >
          Preparing candidate profile summary...
        </Typography>
      </Box>
    );
  }

  const answers = answersData?.answers;
  // Use config data as the primary source of truth for labels and count
  const questions = configData?.questions || answersData?.questions;

  if (!answers || !questions || questions.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 6,
          textAlign: 'center',
          bgcolor: 'rgba(249, 250, 251, 0.5)',
          borderRadius: '24px',
          border: '2px dashed',
          borderColor: 'grey.200',
          m: 3,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            bgcolor: 'background.paper',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid',
            borderColor: 'grey.100',
          }}
        >
          <DescriptionOutlined sx={{ fontSize: 40, color: '#d1d5db' }} />
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
            letterSpacing: '-0.01em',
          }}
        >
          Incomplete Records
        </Typography>
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: '13px',
            maxWidth: 240,
            mx: 'auto',
            mb: 4,
          }}
        >
          We couldn&apos;t find any pre-qualification data for this candidate
          yet.
        </Typography>

        <Button
          variant="contained"
          onClick={() => handleResendInvite(jobApplicantId)}
          disabled={isResending}
          startIcon={
            isResending ? (
              <CircularProgress size={16} sx={{ color: 'white' }} />
            ) : (
              <EmailOutlined sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            background: 'linear-gradient(to right, #9333ea, #ec4899)',
            color: 'white',
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            fontSize: '13px',
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            '&:hover': {
              background: 'linear-gradient(to right, #7e22ce, #db2777)',
              boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
            },
            '&:disabled': {
              background: 'linear-gradient(to right, #c084fc, #f9a8d4)',
              color: 'white',
            },
          }}
          aria-label="Resend pre-qualification invite"
        >
          {isResending ? 'Sending Invitation...' : 'Send Pre Qualifying Invite'}
        </Button>
        {preQualSentAt && (
          <Typography
            sx={{
              mt: 2,
              fontSize: '11px',
              fontWeight: 700,
              color: '#059669',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Pre Qualification Invite sent on:{' '}
            {typeof preQualSentAt === 'number'
              ? new Date(preQualSentAt * 1000).toLocaleString()
              : new Date(preQualSentAt).toLocaleString()}
          </Typography>
        )}
      </Paper>
    );
  }

  // Format the submission date nicely
  const formattedDate = (() => {
    const dateVal = answers.submittedAt;
    if (!dateVal) return 'N/A';
    const date =
      typeof dateVal === 'number' && dateVal < 10000000000
        ? new Date(dateVal * 1000)
        : new Date(dateVal);

    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  })();

  return (
    <>
      {/* Video Modal */}
      <Dialog
        open={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            border: '1px solid',
            borderColor: 'grey.100',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2.5,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '12px',
                bgcolor: '#ecfdf5',
                border: '1px solid',
                borderColor: '#d1fae5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <VideocamOutlined sx={{ fontSize: 20, color: '#059669' }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#1e293b',
                  letterSpacing: '-0.01em',
                }}
              >
                Video Response
              </Typography>
              <Typography
                sx={{
                  fontSize: '11px',
                  color: '#64748b',
                  mt: 0.25,
                  fontWeight: 600,
                  maxWidth: '90%',
                }}
              >
                {selectedVideo?.question}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={() => setSelectedVideo(null)}
            size="small"
            sx={{
              border: '1px solid',
              borderColor: 'grey.100',
              borderRadius: '12px',
              '&:hover': { borderColor: 'grey.200' },
            }}
          >
            <CloseRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5, pt: '0 !important' }}>
          <Box
            sx={{
              bgcolor: '#020617',
              borderRadius: '16px',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: '#0f172a',
              aspectRatio: '16/9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {selectedVideo && (
              <video
                style={{ width: '100%', height: '100%' }}
                src={selectedVideo.url}
                preload="metadata"
                controls
                autoPlay
              >
                Your browser does not support the video tag.
              </video>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* High-Level Profile Summary Header */}
        <Paper
          elevation={0}
          sx={{
            px: 2.5,
            py: 2,
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                bgcolor: '#ecfdf5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid',
                borderColor: '#d1fae5',
                flexShrink: 0,
              }}
            >
              <CheckCircleOutlineRounded
                sx={{ fontSize: 20, color: '#059669' }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#1e293b',
                  letterSpacing: '-0.01em',
                }}
              >
                Questionnaire Profile
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#10b981',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '11px',
                    color: '#94a3b8',
                    fontWeight: 500,
                  }}
                >
                  Completed on {formattedDate}
                </Typography>
              </Stack>
            </Box>
          </Stack>
          <Typography
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontSize: '10px',
              fontWeight: 800,
              color: '#cbd5e1',
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
            }}
          >
            verified applicant
          </Typography>
        </Paper>

        {/* Information Grid - Professional Tiles */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
            gap: 1.5,
          }}
        >
          {[...questions]
            .sort((a, b) => a.order - b.order)
            .map((q, idx) => {
              const value = (() => {
                // Priority 1: Direct key mapping
                let rawVal = answers[q.questionKey || ''];

                // Priority 2: Case-insensitive and dash-to-camel mapping
                if (rawVal === undefined && q.questionKey) {
                  const camelKey = q.questionKey
                    .toLowerCase()
                    .replace(/([-_][a-z])/g, (group) =>
                      group
                        .toUpperCase()
                        .replace('-', '')
                        .replace('_', ''),
                    );

                  const targetKey = q.questionKey.toLowerCase();
                  const foundKey = Object.keys(answers).find(
                    (k) =>
                      k.toLowerCase() === targetKey ||
                      k.toLowerCase() === camelKey.toLowerCase(),
                  );
                  if (foundKey) rawVal = answers[foundKey];
                }

                // Priority 3: Boolean formatting
                if (typeof rawVal === 'boolean')
                  return rawVal ? 'Yes' : 'No';

                // Priority 4: Video data mapping
                if (isVideoQuestionType(q.questionType)) {
                  const videoAnswer = answersData?.videoAnswers?.find(
                    (va) =>
                      va.questionKey &&
                      q.questionKey &&
                      va.questionKey.toLowerCase() ===
                        q.questionKey.toLowerCase(),
                  );
                  const rawVideoAnswer = q.questionKey
                    ? answersData?.answers?.[q.questionKey]
                    : null;
                  const rawVideoKey =
                    typeof rawVideoAnswer === 'string'
                      ? rawVideoAnswer
                      : rawVideoAnswer?.videoKey ||
                        rawVideoAnswer?.url ||
                        rawVideoAnswer?.value;

                  return (
                    getVideoStoragePath(
                      videoAnswer?.videoKey || videoAnswer?.url,
                    ) ||
                    getVideoStoragePath(rawVideoKey) ||
                    rawVal ||
                    null
                  );
                }

                // Priority 5: Location data mapping
                if (q.questionType === 'location') {
                  const locAns = answersData?.locationAnswers?.find(
                    (la) =>
                      la.questionKey &&
                      q.questionKey &&
                      la.questionKey.toLowerCase() ===
                        q.questionKey.toLowerCase(),
                  );
                  if (locAns) {
                    return `${locAns.city}${
                      locAns.state ? `, ${locAns.state}` : ''
                    }, ${locAns.country}`;
                  }
                }

                return rawVal !== undefined ? String(rawVal) : null;
              })();

              if (!value) return null;

              return (
                <Paper
                  key={q.questionKey || idx}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: '#a7f3d0',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                    },
                    ...(isVideoQuestionType(q.questionType) && {
                      gridColumn: { sm: 'span 2', lg: 'span 2' },
                    }),
                  }}
                >
                  <Stack spacing={0.75}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 0.5 }}
                    >
                      <Typography
                        sx={{
                          fontSize: '10px',
                          fontWeight: 900,
                          color: '#cbd5e1',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          transition: 'color 0.2s',
                          '.MuiPaper-root:hover &': {
                            color: '#10b981',
                          },
                        }}
                      >
                        {(idx + 1).toString().padStart(2, '0')}.{' '}
                        {q.questionType.replace('-', ' ')}
                      </Typography>
                      {q.questionType === 'location' && (
                        <LocationOnOutlined
                          sx={{ fontSize: 12, color: '#cbd5e1' }}
                        />
                      )}
                    </Stack>
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 700,
                        color: '#64748b',
                        textTransform: 'uppercase',
                        lineHeight: 1.4,
                      }}
                    >
                      {q.question}
                    </Typography>
                    {isVideoQuestionType(q.questionType) ? (
                      <Box sx={{ mt: 1.5 }}>
                        {videoUrls[q.questionKey || ''] ? (
                          <Button
                            variant="outlined"
                            onClick={() =>
                              setSelectedVideo({
                                url: videoUrls[q.questionKey || ''],
                                question: q.question,
                              })
                            }
                            startIcon={
                              <VideocamOutlined
                                sx={{ fontSize: 16, color: '#059669' }}
                              />
                            }
                            sx={{
                              color: '#15803d',
                              bgcolor: '#ecfdf5',
                              borderColor: '#a7f3d0',
                              borderRadius: '16px',
                              fontSize: '12px',
                              fontWeight: 700,
                              textTransform: 'none',
                              px: 2,
                              py: 1.5,
                              '&:hover': {
                                bgcolor: '#d1fae5',
                                borderColor: '#a7f3d0',
                              },
                            }}
                          >
                            View Live Response
                          </Button>
                        ) : (
                          <Box
                            sx={{
                              borderRadius: '16px',
                              border: '1px solid',
                              borderColor: videoErrors[q.questionKey || '']
                                ? '#fecdd3'
                                : '#d1fae5',
                              bgcolor: videoErrors[q.questionKey || '']
                                ? '#fff1f2'
                                : 'rgba(236, 253, 245, 0.5)',
                              p: 2,
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="flex-start"
                              spacing={1.5}
                            >
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: '12px',
                                  bgcolor: 'background.paper',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1px solid',
                                  borderColor: '#d1fae5',
                                  flexShrink: 0,
                                }}
                              >
                                {videoErrors[q.questionKey || ''] ? (
                                  <WarningAmberRounded
                                    sx={{ fontSize: 16, color: '#e11d48' }}
                                  />
                                ) : (
                                  <VideocamOutlined
                                    sx={{ fontSize: 16, color: '#059669' }}
                                  />
                                )}
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    color: videoErrors[q.questionKey || '']
                                      ? '#be123c'
                                      : '#15803d',
                                  }}
                                >
                                  {videoErrors[q.questionKey || '']
                                    ? 'Video could not be loaded'
                                    : videoLoading[q.questionKey || '']
                                      ? 'Loading video response...'
                                      : 'Video response saved'}
                                </Typography>
                                <Typography
                                  sx={{
                                    mt: 0.5,
                                    fontSize: '10px',
                                    fontFamily: 'monospace',
                                    color: 'rgba(21, 128, 61, 0.7)',
                                    wordBreak: 'break-all',
                                  }}
                                >
                                  {value}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 700,
                          color: '#1e293b',
                          letterSpacing: '-0.01em',
                          lineHeight: 1.4,
                          wordBreak: 'break-word',
                          mt: 0.5,
                        }}
                      >
                        {value}
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              );
            })}
        </Box>

        {/* Signature Footer - Professional Stamp */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{
            pt: 3,
            pb: 1,
            borderTop: '1px solid',
            borderColor: 'grey.50',
            opacity: 0.3,
          }}
        >
          <Typography
            sx={{
              fontSize: '8px',
              fontWeight: 900,
              letterSpacing: '0.15em',
              color: '#94a3b8',
              textTransform: 'uppercase',
            }}
          >
            Security Verified
          </Typography>
          <Box
            sx={{
              width: 4,
              height: 4,
              borderRadius: '50%',
              bgcolor: '#cbd5e1',
            }}
          />
          <Typography
            sx={{
              fontSize: '8px',
              fontWeight: 900,
              letterSpacing: '0.15em',
              color: '#94a3b8',
              textTransform: 'uppercase',
            }}
          >
            HiAcuity IntelliGate
          </Typography>
        </Stack>
      </Box>
    </>
  );
};
